import React, {useMemo} from 'react';
import {ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Icon, Label} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {groupingCellPaths, groupingTokensFor, GroupingToken, isProducibleGroupingToken} from './ReportTable/useClientGrouping';
import {fieldByKey, ReportTaxonomy} from './contracts/report-taxonomy.types';
import {grainRootOf} from './data/definitionFields';

/** Translated names for the task grain's eight tokens. `spint` is the legacy backend spelling for
 * 'sprint' (see useClientGrouping.ts) and is intentionally labelled 'Sprint' here, never exposed to
 * the user as 'spint'. Another grain's tokens are named by its catalog instead, which is where every
 * other surface in the builder already reads its field names from. */
export const GROUPING_TOKEN_LABEL_KEY: Record<string, string> = {
	project: 'ai_report_builder.group_by_token.project',
	phase: 'ai_report_builder.group_by_token.phase',
	client: 'ai_report_builder.group_by_token.client',
	role: 'ai_report_builder.group_by_token.role',
	spint: 'ai_report_builder.group_by_token.sprint',
	task: 'ai_report_builder.group_by_token.task',
	assignee: 'ai_report_builder.group_by_token.assignee',
	deliverable: 'ai_report_builder.group_by_token.deliverable',
};

/** definition contract caps grouping at 4 ordered levels (see useClientGrouping.ts's `groupRows`). */
const MAX_GROUPING_TOKENS = 4;

/** Ordered multi-select of up to 4 grouping tokens. Selected tokens render as removable rows (in
 * selection order); a trailing "add group level" trigger offers the remaining tokens and is
 * hidden once the cap is reached. Operates purely through `useReportBuilder()`'s
 * `grouping`/`setGrouping` — it holds no local selection state of its own. */
/** A token's name as the catalog labels its field, or the raw token so it is never blank. */
function catalogTokenLabel(token: GroupingToken, taxonomy: ReportTaxonomy | null, cellPaths: Record<string, string>): string {
	const cellPath = cellPaths[token];
	const label = taxonomy && cellPath ? fieldByKey(taxonomy, cellPath)?.label : undefined;
	return label ?? token;
}

/** A token's display name: the translated label for one of the task tokens, the catalog's own label
 * for anything else, and the raw token as a last resort so an option is never blank.
 *
 * Takes the resolved token -> cell-path map rather than the root, so the map is built once for the
 * control instead of once per token per render. */
function TokenLabel({
	token,
	taxonomy,
	cellPaths,
}: {
	token: GroupingToken;
	taxonomy: ReportTaxonomy | null;
	cellPaths: Record<string, string>;
}) {
	const messageId = GROUPING_TOKEN_LABEL_KEY[token];
	if (messageId) {
		return <FormattedMessage id={messageId} />;
	}
	return <>{catalogTokenLabel(token, taxonomy, cellPaths)}</>;
}

/** The prefix a custom-field grouping token carries before the field's label. */
const CUSTOM_FIELD_TOKEN_PREFIX = 'customFields.';

/** A reader-facing name for a token inside a sentence, or null when there is none.
 *
 * Used for tokens the report cannot group by, which are exactly the ones the catalog has no field
 * for, so the catalog label is usually missing. A task token has its own translated name, and a
 * custom-field token carries its label after the prefix; anything else has no name a reader would
 * recognise, and printing the token's wire spelling would be worse than not naming it. */
export function groupingTokenName(
	token: GroupingToken,
	taxonomy: ReportTaxonomy | null,
	cellPaths: Record<string, string>,
	formatMessage: (descriptor: {id: string}) => string
): string | null {
	const messageId = GROUPING_TOKEN_LABEL_KEY[token];
	if (messageId) {
		return formatMessage({id: messageId});
	}
	const cellPath = cellPaths[token];
	const catalogLabel = taxonomy && cellPath ? fieldByKey(taxonomy, cellPath)?.label : undefined;
	if (catalogLabel) {
		return catalogLabel;
	}
	if (token.startsWith(CUSTOM_FIELD_TOKEN_PREFIX)) {
		return token.slice(CUSTOM_FIELD_TOKEN_PREFIX.length) || null;
	}
	return null;
}

export function GroupByControl(): JSX.Element {
	const {formatMessage} = useIntl();
	const {grouping, setGrouping, definition, taxonomy} = useReportBuilder();

	// What a report may be grouped by is the grain's business, not this control's: the eight task
	// tokens and the project grain's seven share only `client`, and offering the wrong set produces
	// a control that groups every row into one bucket named after a field the report has not got.
	const root = grainRootOf(definition);
	//
	// And only a token that can actually be produced: one whose field the catalog does not publish
	// cannot be fetched, so offering it would offer exactly that one-bucket report.
	const cellPaths = useMemo(() => groupingCellPaths(root, taxonomy), [root, taxonomy]);
	const offerableTokens = useMemo(
		() => groupingTokensFor(root, taxonomy).filter(token => isProducibleGroupingToken(token, cellPaths, taxonomy)),
		[root, taxonomy, cellPaths]
	);

	const availableTokens = useMemo(
		() => offerableTokens.filter(token => !grouping.includes(token)),
		[offerableTokens, grouping]
	);

	const atCap = grouping.length >= MAX_GROUPING_TOKENS;

	const addToken = (token: GroupingToken): void => {
		if (atCap || grouping.includes(token)) {
			return;
		}
		setGrouping([...grouping, token]);
	};

	const removeToken = (token: GroupingToken): void => {
		setGrouping(grouping.filter(t => t !== token));
	};

	return (
		<FlexColumn gap={'xs'} flex={'0 0 auto'}>
			{/* the design's "Group By" control is a labeled `Dropdown field` instance
			 * (componentProperties `Label Show: true`, `Label text: 'Group By'`) — a visible caption
			 * above a bordered field-style trigger, not a bare button.
			 * `ButtonDropdownField` is the DS component for the field itself; the caption is a
			 * sibling `Label`, matching the `FlexColumn`-wrapped `Label` + field pattern used
			 * elsewhere (e.g. ChangeViewGroupings.tsx's `select-grouping` field). This also
			 * differentiates Group By's trigger from Columns', which stays a plain `ButtonDropdown`
			 * per the design's `Filter` component instance. */}
			<Label data-cy={'ai-report-group-by-label'}>
				<FormattedMessage id={'ai_report_builder.group_by_label'} />
			</Label>
			<DropdownList>
				<DropdownList.Trigger>
					{/* the design: sizes the Group By field at ~180px, not full-bleed;
					 * `ButtonDropdownField` defaults to width 100%, so pin it explicitly. */}
					<ButtonDropdownField width={'180px'} isDefaultLabel={grouping.length === 0} data-cy={'ai-report-group-by'}>
						<FormattedMessage id={'ai_report_builder.group_by'} />
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					<DropdownList.ContentGroup title={formatMessage({id: 'ai_report_builder.group_by'})}>
						{grouping.map((token, index) => (
							<DropdownList.Item
								key={token}
								onClick={() => removeToken(token)}
								data-cy={`ai-report-group-by-selected-${token}`}
							>
								<FlexRow justifyContent={'space-between'} alignItems={'center'}>
									<span>
										{index + 1}. <TokenLabel token={token} taxonomy={taxonomy} cellPaths={cellPaths} />
									</span>
									<Icon icon={'close'} />
								</FlexRow>
							</DropdownList.Item>
						))}
						{!atCap && availableTokens.length > 0 ? (
							<DropdownList.DropdownItem>
								<DropdownList.Trigger>
									<ButtonDropdownField isDefaultLabel data-cy={'ai-report-group-by-add'}>
										<FormattedMessage id={'ai_report_builder.group_by_add'} />
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content offset={4} useTriggerWidth>
									{availableTokens.map(token => (
										<DropdownList.Item
											key={token}
											onClick={() => addToken(token)}
											data-cy={`ai-report-group-by-option-${token}`}
										>
											<TokenLabel token={token} taxonomy={taxonomy} cellPaths={cellPaths} />
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList.DropdownItem>
						) : null}
					</DropdownList.ContentGroup>
				</DropdownList.Content>
			</DropdownList>
		</FlexColumn>
	);
}
